import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Send, Loader2, Link2, Copy, ExternalLink, Unlink, Server, RefreshCcw, Users } from "lucide-react";
import { toast } from "sonner";

export default function TelegramCard() {
  const { user } = useAuth();
  const isAdmin = (user?.roles || []).some((r) => ["admin", "administracion"].includes(r));

  const [status, setStatus] = useState(null);
  const [tokenData, setTokenData] = useState(null); // {token, deep_link, bot_username}
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [adminStatus, setAdminStatus] = useState(null);
  const [adminLoading, setAdminLoading] = useState(false);

  const load = useCallback(() => {
    api.get("/telegram/link/status")
      .then(({ data }) => setStatus(data))
      .catch(() => setStatus({ linked: false, bot_username: "" }));
  }, []);

  const loadAdmin = useCallback(() => {
    if (!isAdmin) return;
    api.get("/telegram/admin/status")
      .then(({ data }) => setAdminStatus(data))
      .catch(() => {});
  }, [isAdmin]);

  useEffect(() => { load(); loadAdmin(); }, [load, loadAdmin]);

  const refrescarWebhook = async () => {
    if (!confirm("Se registrará el webhook apuntando a APP_PUBLIC_URL. Úsalo tras un redeploy a producción o para reactivar el bot.")) return;
    setAdminLoading(true);
    try {
      const { data } = await api.post("/telegram/admin/set-webhook");
      toast.success(`Webhook actualizado: ${data.webhook_url}`);
      loadAdmin();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally {
      setAdminLoading(false);
    }
  };

  const generar = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/telegram/link/generate");
      setTokenData(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error generando código");
    } finally {
      setLoading(false);
    }
  };

  const copiar = async (txt, etiqueta) => {
    try {
      await navigator.clipboard.writeText(txt);
      toast.success(`${etiqueta} copiado ✓`);
    } catch {
      toast.info("Copia manual el texto");
    }
  };

  const desvincular = async () => {
    if (!confirm("¿Desvincular tu Telegram? Dejarás de recibir avisos por Telegram.")) return;
    try {
      await api.post("/telegram/link/unlink");
      toast.success("Desvinculado");
      setTokenData(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
  };

  const toggleOptIn = async (val) => {
    try {
      await api.post("/telegram/opt-in", { opt_in: val });
      setStatus((s) => ({ ...s, opt_in: val }));
      toast.success(val ? "Recibirás avisos por Telegram" : "Avisos por Telegram desactivados");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
  };

  const enviarPrueba = async () => {
    setSending(true);
    try {
      await api.post("/telegram/admin/test", {});
      toast.success("Mensaje enviado · revisa tu Telegram");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo enviar (¿eres admin?)");
    } finally {
      setSending(false);
    }
  };

  const botConfigured = !!status?.bot_username;
  const linked = !!status?.linked;

  return (
    <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-5 mt-4" data-testid="perfil-telegram">
      <div className="flex items-center gap-2 text-sky-300 font-semibold mb-3">
        <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current" aria-hidden="true">
          <path d="M9.417 15.181l-.397 5.584c.568 0 .814-.244 1.109-.537l2.663-2.545 5.518 4.041c1.012.557 1.725.264 1.998-.937l3.622-16.972.001-.001c.321-1.496-.541-2.081-1.527-1.714L1.116 9.462C-.322 10.026-.301 10.836.87 11.201l5.19 1.621L18.114 5.24c.567-.375 1.083-.167.658.207L9.417 15.181z"/>
        </svg>
        Notificaciones por Telegram
        <span className="ml-auto text-[10px] px-2 py-0.5 rounded bg-sky-500/15 text-sky-200 uppercase tracking-wider">Nuevo</span>
      </div>
      <p className="text-xs text-zinc-400 mb-4">
        Telegram sustituye a WhatsApp para avisos internos (rutas, fichajes, partes). Sin coste, sin ventana 24 h,
        sin plantillas. Se mantiene WhatsApp para mensajes a clientes.
      </p>

      {!botConfigured && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded p-3 text-xs text-amber-200 mb-3">
          ⏳ El bot aún no está configurado. Cuando el administrador cree el bot en Telegram, este panel se activará.
        </div>
      )}

      {botConfigured && linked && (
        <div className="space-y-3">
          <div className="flex items-center gap-3 bg-emerald-500/5 border border-emerald-500/30 rounded p-3">
            <div className="w-9 h-9 rounded-full bg-emerald-500/20 flex items-center justify-center">
              <Link2 className="w-4 h-4 text-emerald-300" />
            </div>
            <div className="flex-1">
              <div className="text-sm text-emerald-200 font-medium">Cuenta vinculada</div>
              <div className="text-xs text-zinc-400">
                {status.telegram_username ? `@${status.telegram_username}` : "Telegram conectado"}
                {status.linked_at && ` · desde ${status.linked_at.slice(0, 10)}`}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Label htmlFor="tg-opt" className="text-sm cursor-pointer">Recibir avisos por Telegram</Label>
            <Switch
              id="tg-opt"
              checked={!!status.opt_in}
              onCheckedChange={toggleOptIn}
              data-testid="tg-opt"
            />
          </div>

          <div className="flex flex-wrap gap-2">
            <Button onClick={enviarPrueba} disabled={sending}
              variant="outline" size="sm"
              className="bg-transparent border-sky-700 text-sky-200"
              data-testid="tg-test-btn">
              {sending ? <><Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> Enviando…</> : <><Send className="w-3.5 h-3.5 mr-2" /> Enviar prueba</>}
            </Button>
            <Button onClick={desvincular} variant="ghost" size="sm"
              className="text-red-300 hover:text-red-200"
              data-testid="tg-unlink-btn">
              <Unlink className="w-3.5 h-3.5 mr-2" /> Desvincular
            </Button>
          </div>
        </div>
      )}

      {botConfigured && !linked && !tokenData && (
        <Button onClick={generar} disabled={loading}
          className="bg-sky-500 hover:bg-sky-400 text-zinc-900 font-semibold"
          data-testid="tg-link-btn">
          {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generando…</> : <><Link2 className="w-4 h-4 mr-2" /> Vincular Telegram</>}
        </Button>
      )}

      {botConfigured && !linked && tokenData && (
        <div className="space-y-3">
          <div className="text-sm text-zinc-300">Paso 1: abre el bot en Telegram</div>
          <a href={tokenData.deep_link} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 bg-sky-500 hover:bg-sky-400 rounded text-zinc-900 font-semibold text-sm"
            data-testid="tg-open-bot">
            <ExternalLink className="w-4 h-4" /> Abrir @{tokenData.bot_username}
          </a>

          <div className="text-sm text-zinc-300 pt-2">
            Paso 2 (alternativa manual): en el bot escribe
            <code className="mx-2 bg-zinc-800 px-2 py-0.5 rounded text-sky-200 font-mono text-xs">/vincular {tokenData.token}</code>
            <Button size="xs" variant="ghost" onClick={() => copiar(`/vincular ${tokenData.token}`, "Comando")}
              className="text-zinc-400 hover:text-zinc-200 h-6 px-1.5 align-middle"
              data-testid="tg-copy-cmd">
              <Copy className="w-3 h-3" />
            </Button>
          </div>

          <div className="text-xs text-amber-300/80 mt-2">
            ⌛ Código válido {tokenData.expires_min} min · un solo uso.
          </div>
          <div className="flex gap-2">
            <Button onClick={generar} variant="outline" size="sm"
              className="bg-transparent border-zinc-700 text-zinc-300"
              data-testid="tg-regenerate">
              Generar código nuevo
            </Button>
            <Button onClick={() => { setTokenData(null); load(); }}
              variant="ghost" size="sm" className="text-zinc-400"
              data-testid="tg-recheck">
              Ya lo hice, refrescar
            </Button>
          </div>
        </div>
      )}

      {/* ========= Panel admin ========= */}
      {isAdmin && adminStatus && (
        <div className="mt-5 pt-4 border-t border-zinc-800" data-testid="tg-admin-panel">
          <div className="flex items-center gap-2 text-zinc-300 text-xs uppercase tracking-wider mb-3">
            <Server className="w-3.5 h-3.5" /> Administración del bot
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
            <div className="bg-zinc-950/60 border border-zinc-800 rounded p-2">
              <div className="text-[10px] text-zinc-500 uppercase">Estado</div>
              <div className={`text-sm font-semibold ${adminStatus.configured ? "text-emerald-300" : "text-red-300"}`}>
                {adminStatus.configured ? "Conectado" : "Sin token"}
              </div>
            </div>
            <div className="bg-zinc-950/60 border border-zinc-800 rounded p-2">
              <div className="text-[10px] text-zinc-500 uppercase">Bot</div>
              <div className="text-sm text-zinc-200 truncate">@{adminStatus.bot_username || "—"}</div>
            </div>
            <div className="bg-zinc-950/60 border border-zinc-800 rounded p-2">
              <div className="text-[10px] text-zinc-500 uppercase flex items-center gap-1"><Users className="w-2.5 h-2.5" /> Vinculados</div>
              <div className="text-sm text-zinc-200 font-mono">
                {adminStatus.users_linked}/{adminStatus.users_total}
              </div>
            </div>
            <div className="bg-zinc-950/60 border border-zinc-800 rounded p-2">
              <div className="text-[10px] text-zinc-500 uppercase">Pendientes</div>
              <div className="text-sm text-zinc-200 font-mono">
                {adminStatus.webhook_info?.pending_update_count ?? 0}
              </div>
            </div>
          </div>

          <div className="bg-zinc-950/60 border border-zinc-800 rounded p-2 mb-3">
            <div className="text-[10px] text-zinc-500 uppercase mb-1">Webhook activo</div>
            <div className="text-xs text-zinc-300 font-mono break-all">
              {adminStatus.webhook_info?.url || <span className="text-amber-300">— sin registrar —</span>}
            </div>
            {adminStatus.webhook_info?.last_error_message && (
              <div className="text-[11px] text-red-300 mt-1">
                Último error: {adminStatus.webhook_info.last_error_message}
              </div>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            <Button onClick={refrescarWebhook} disabled={adminLoading || !adminStatus.configured}
              size="sm" variant="outline"
              className="bg-transparent border-sky-700 text-sky-200"
              data-testid="tg-webhook-refresh"
              title="Registra el webhook apuntando a APP_PUBLIC_URL (útil tras un redeploy)">
              {adminLoading ? <><Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> Actualizando…</> : <><RefreshCcw className="w-3.5 h-3.5 mr-2" /> Actualizar webhook</>}
            </Button>
            <Button onClick={loadAdmin}
              size="sm" variant="ghost"
              className="text-zinc-400"
              data-testid="tg-admin-reload">
              Refrescar estado
            </Button>
          </div>

          <div className="text-[10px] text-zinc-500 mt-2">
            💡 Tras cada redeploy a producción, pulsa &quot;Actualizar webhook&quot; para volver a apuntar Telegram al dominio productivo.
          </div>
        </div>
      )}
    </div>
  );
}
